/**
 * Prepared ACH mandate and third-party disclosure copy.
 *
 * The copy is a working draft that has not had legal review. The version
 * identifier changes whenever the wording does, so an evidence row names the
 * exact text its subscriber saw.
 *
 * Placeholders are interpolated by the renderer:
 * `{amount}` — e.g. "$10.00/month"; `{institution}` — bank display name;
 * `{mask}` — last-four account mask; `{firstChargeDate}` — first debit date.
 *
 * The amount and first-charge date come from the server rather than being
 * computed client-side: the api records its own `amountDisplay` and
 * `firstChargeDisplay` on the `mandates` row, and a rendered string that
 * disagrees with the stored one defeats what the row exists to prove.
 *
 * The disclosure deliberately does not name the account owner. The owner-name
 * check returns a score band, never a name, and it cannot tell a mismatch from
 * an unavailable check — so "may not be held in your name" is the only
 * phrasing accurate in both states. Sourcing a real name would also mean
 * showing one person's bank-held name to another who was just flagged as
 * possibly not being them.
 */
export const MANDATE_TEXT_VERSION = 'draft-2';

/** Every version the server accepts as evidence; grows when copy changes, never shrinks. */
export const KNOWN_MANDATE_TEXT_VERSIONS: readonly string[] = ['draft-1', MANDATE_TEXT_VERSION];

export const MANDATE_TEXT_DRAFT = `By selecting "Start free trial," you authorize Mantle to debit your {institution} account (••{mask}) {amount}, starting {firstChargeDate} and recurring until you cancel. $0 is charged today. You can cancel anytime by contacting support; to stop a scheduled debit, cancel at least 3 business days before it is due. Debits appear on your statement as MANTLE SUBSCRIPTION.`;

export const THIRD_PARTY_DISCLOSURE_DRAFT = `The account you selected (••{mask}) may not be held in your name. By checking this box, you confirm you are authorized to make payments from this account.`;

/**
 * Prepared ACH mandate copy for switching the debited account mid-membership.
 *
 * Versioned separately from the subscribe mandate above, and validated
 * against its own list, because it authorizes a different moment: the member
 * is not starting a trial, nothing is charged at switch time, and the next
 * debit lands on the membership's existing schedule. Accepting a subscribe
 * version here would store evidence naming copy the member was never shown.
 *
 * Same placeholders and drafting caveats as the subscribe mandate.
 */
export const SWITCH_MANDATE_TEXT_VERSION = 'switch-draft-1';

/** Every switch-mandate version the server accepts as evidence; grows, never shrinks. */
export const KNOWN_SWITCH_MANDATE_TEXT_VERSIONS: readonly string[] = [SWITCH_MANDATE_TEXT_VERSION];

export const SWITCH_MANDATE_TEXT_DRAFT = `By selecting "Switch payment account," you authorize Mantle to debit your {institution} account (••{mask}) {amount}, starting {firstChargeDate} and recurring until you cancel. Nothing is charged today; this replaces the account your membership already debits. You can cancel anytime by contacting support; to stop a scheduled debit, cancel at least 3 business days before it is due. Debits appear on your statement as MANTLE SUBSCRIPTION.`;

/**
 * Prepared recurring-card consent copy, for the fallback rail.
 *
 * Versioned separately from the bank-debit mandate above, and validated
 * against its own list, because the two are different authorizations: this one
 * carries no bank account and answers to card network rules rather than Nacha.
 * Accepting either version on either rail would let an evidence row name copy
 * the subscriber was never shown.
 *
 * Placeholders are interpolated by the renderer: `{amount}` and
 * `{firstChargeDate}`. The card itself is deliberately not named: the
 * consent screen shows the entered card alongside, so the copy carries no
 * placeholder a renderer would need a card-detail source for.
 *
 * A working draft that has not had legal review.
 */
export const CARD_TERMS_VERSION = 'card-draft-1';

/** Every card-consent version the server accepts as evidence; grows, never shrinks. */
export const KNOWN_CARD_TERMS_VERSIONS: readonly string[] = [CARD_TERMS_VERSION];

export const CARD_TERMS_DRAFT = `By starting your free trial, you authorize Mantle to charge your card {amount}, starting {firstChargeDate} and recurring until you cancel. $0 is charged today. You can cancel anytime by contacting support. Charges appear on your statement as MANTLE SUBSCRIPTION.`;
